import { AggregationType, CategoryOrderBy, GroupDateType, TimeSeriesResponse } from '@carto/api-client'
import { TIME_SERIES_CHART_TYPES, TimeSeriesData } from '@carto/meridian-ds/widgets'
import { KeplerAggregationType } from './kepler-types'

export enum WidgetType {
  CATEGORY = 'category',
  FORMULA = 'formula',
  HISTOGRAM = 'histogram',
  TIMESERIES = 'timeseries',
  TABLE = 'table',
  RANGE = 'range',
  PIE = 'pie'
}

export enum NoteFormat {
  PlainText = 'plainText',
  Markdown = 'markdown'
}

export interface BaseWidgetConfig {
  /** ID for the widget instance. */
  id: string
  /** Title to show in the widget header. */
  title: string
  /** ID of the data source to get the data from. */
  dataSource: string
  /** Name of primary dataset column for widget. */
  column?: string
  operation?: AggregationType
  spatialIndexAggregation?: KeplerAggregationType // those two look this look fishy as hell!
  formatter?: string
  note?: string
  noteFormat?: NoteFormat
  isValid: boolean
  collapsible?: boolean
  autoCollapse?: boolean
  global?: boolean
  noteExpanded?: boolean
}

export interface FilterableWidget {
  disableFiltering?: boolean
  crossFilteringDataSourceIds?: string[]
}

export interface FormulaWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.FORMULA
  operationExp?: string
}

export interface CategoryWidgetConfig extends BaseWidgetConfig, FilterableWidget {
  type: WidgetType.CATEGORY
  operationColumn: string
  operationExp?: string
  orderBy?: CategoryOrderBy
  spatialIndexAggregationOperationColumn?: KeplerAggregationType
}

export interface HistogramWidgetConfig extends BaseWidgetConfig, FilterableWidget {
  type: WidgetType.HISTOGRAM
  buckets: number
  xAxisFormatter?: string
  min?: number
  max?: number
}

export type TimeSeriesWidgetSerie = {
  operation: AggregationType
  operationColumn: string
  operationExp?: string
  customMetricLabel?: string
}

export type TimeSeriesDataAndCategories = TimeSeriesResponse & {
  rows: TimeSeriesData[]
  categories?: string[]
  options?: {
    splitByCategory?: boolean
    series?: TimeSeriesWidgetSerie[]
    operation?: AggregationType
  }
}

export interface TimeSeriesWidgetConfig extends BaseWidgetConfig, FilterableWidget {
  type: WidgetType.TIMESERIES
  operationColumn: string
  operationExp?: string
  customMetricLabel?: string
  /** Step applied to group the data. */
  stepSize: GroupDateType
  /** Multiplier applied to `stepSize`. Use to aggregate by 2 hours, 5 seconds, etc. */
  stepMultiplier?: number
  /** Chart used to represent the time series. */
  chartType: TIME_SERIES_CHART_TYPES
  /** Enable/disable animation controls (play, pause, stop, speed). Default: true. */
  showControls: boolean
  /** Name of the data source's column to split the data by category. */
  splitByCategory?: string
  /** Select specific categories. Default: Most 'dominant' categories */
  splitByCategoryValues?: string[]
  /** Limit of categories shown. Default: 5. */
  splitByCategoryLimit?: number
  /** Array of {operation, operationColumn} objects that specify multiple aggregations. */
  series?: TimeSeriesWidgetSerie[]
  /** Height of the chart. Default: 22 x theme.spacingValue. */
  height?: string
}

export interface PieWidgetConfig extends BaseWidgetConfig, FilterableWidget {
  type: WidgetType.PIE
  operationColumn: string
  operationExp?: string
  // TODO: this something not used anywhere
  spatialIndexAggregationOperationColumn?: KeplerAggregationType
}

export enum TableColumnType {
  Number = 'number',
  String = 'string',
  Date = 'date'
}

export interface TableColumn {
  field: string
  headerName?: string
  format?: string
  type?: TableColumnType
  spatialIndexAggregation?: KeplerAggregationType
}

export interface TableWidgetConfig extends BaseWidgetConfig {
  type: WidgetType.TABLE
  columns: TableColumn[]
  dense?: boolean
  pageSize?: number
}

export interface RangeWidgetConfig extends BaseWidgetConfig, FilterableWidget {
  type: WidgetType.RANGE
  min?: number
  max?: number
}

export type WidgetConfig =
  | CategoryWidgetConfig
  | FormulaWidgetConfig
  | HistogramWidgetConfig
  | RangeWidgetConfig
  | TimeSeriesWidgetConfig
  | TableWidgetConfig
  | PieWidgetConfig
